import React, { useEffect, useRef, useState } from 'react';
import { apiFetch, apiDownload } from '../lib/api';

interface AllegatoAcquirente {
  id: number;
  nome: string;
  nomeFile: string;
  dimensione: number | null;
  ivaAgevolata: boolean;
  uploadedAt: string;
}

interface Props {
  acquirenteId: number;
  acquirenteUnita: string;
  acquirenteNomeCompleto: string;
  /** chiamata dopo upload/edit/delete riuscito */
  onChange?: () => void;
}

const PaperclipIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

function formatBytes(b: number | null): string {
  if (b == null) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('it-IT');
}

export default function GestioneAllegatiAcquirente({
  acquirenteId,
  acquirenteUnita,
  acquirenteNomeCompleto,
  onChange,
}: Props) {
  const [allegati, setAllegati] = useState<AllegatoAcquirente[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [fileNuovo, setFileNuovo] = useState<File | null>(null);
  const [nomeNuovo, setNomeNuovo] = useState('');
  const [ivaAgevolataNuovo, setIvaAgevolataNuovo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const ricarica = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // @api GET /api/acquirenti/:acquirenteId/allegati -> {"success":"true","data":[{"id":"number","nome":"string","nomeFile":"string","dimensione":"number","ivaAgevolata":"boolean","uploadedAt":"string"}]}
      const res = await apiFetch<{ success: boolean; data: AllegatoAcquirente[] }>(
        `/api/acquirenti/${acquirenteId}/allegati`
      );
      setAllegati(res.data || []);
    } catch (err) {
      console.error('[GestioneAllegatiAcquirente] errore caricamento:', err);
      setAllegati([]);
      setLoadError((err as Error).message || 'Errore nel caricamento allegati');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    ricarica();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acquirenteId]);

  const upload = async () => {
    if (!fileNuovo || !nomeNuovo.trim()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', fileNuovo);
      fd.append('nome', nomeNuovo.trim());
      fd.append('ivaAgevolata', String(ivaAgevolataNuovo));
      // @api POST /api/acquirenti/:acquirenteId/allegati {"file":"File","nome":"string","ivaAgevolata":"boolean"} -> {"success":"true"}
      await apiFetch(`/api/acquirenti/${acquirenteId}/allegati`, {
        method: 'POST',
        body: fd,
      });
      setFileNuovo(null);
      setNomeNuovo('');
      setIvaAgevolataNuovo(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await ricarica();
      onChange?.();
    } catch (err) {
      alert((err as Error).message || 'Errore caricamento allegato');
    } finally {
      setSaving(false);
    }
  };

  const aggiorna = async (
    allegatoId: number,
    patch: { nome?: string; ivaAgevolata?: boolean }
  ) => {
    try {
      // @api PUT /api/acquirenti/:acquirenteId/allegati/:allegatoId {"nome":"string?","ivaAgevolata":"boolean?"} -> {"success":"true"}
      await apiFetch(`/api/acquirenti/${acquirenteId}/allegati/${allegatoId}`, {
        method: 'PUT',
        body: JSON.stringify(patch),
      });
      await ricarica();
      onChange?.();
    } catch (err) {
      alert((err as Error).message || 'Errore aggiornamento allegato');
    }
  };

  const elimina = async (allegatoId: number, nome: string) => {
    if (!window.confirm(`Eliminare l'allegato "${nome}"?`)) return;
    try {
      // @api DELETE /api/acquirenti/:acquirenteId/allegati/:allegatoId -> {"success":"true"}
      await apiFetch(`/api/acquirenti/${acquirenteId}/allegati/${allegatoId}`, {
        method: 'DELETE',
      });
      await ricarica();
      onChange?.();
    } catch (err) {
      alert((err as Error).message || 'Errore eliminazione allegato');
    }
  };

  return (
    <div className="bop-allegati-acq">
      <div className="bop-allegati-acq__header">
        <span className="bop-allegati-acq__header-icon">
          <PaperclipIcon />
        </span>
        <span className="bop-allegati-acq__header-title">Allegati acquirente</span>
      </div>
      <div className="bop-allegati-acq__banner">
        Dichiarazioni IVA agevolata, copie di documenti e altri allegati di
        questo acquirente ({acquirenteUnita} — {acquirenteNomeCompleto}). Gli
        stessi allegati sono visibili anche aprendo un preventivo intestato a
        questo acquirente.
      </div>

      {loading ? (
        <div className="bop-allegati-acq__empty">Caricamento allegati…</div>
      ) : loadError ? (
        <div className="bop-allegati-acq__empty bop-text-danger">{loadError}</div>
      ) : allegati.length === 0 ? (
        <div className="bop-allegati-acq__empty">Nessun allegato presente.</div>
      ) : (
        <div className="bop-allegati-acq__lista">
          {allegati.map(a => (
            <div key={a.id} className="bop-allegati-acq__riga">
              <div className="bop-allegati-acq__riga-info">
                <input
                  className="bop-input bop-allegati-acq__nome"
                  defaultValue={a.nome}
                  onBlur={e => {
                    const nv = e.target.value.trim();
                    if (nv && nv !== a.nome) aggiorna(a.id, { nome: nv });
                  }}
                />
                <div className="bop-allegati-acq__file">
                  {a.nomeFile}
                  {a.dimensione != null ? ` · ${formatBytes(a.dimensione)}` : ''}
                  {' · '}caricato il {formatDate(a.uploadedAt)}
                </div>
              </div>
              <label className="bop-allegati-acq__iva-label">
                <input
                  type="checkbox"
                  checked={a.ivaAgevolata}
                  onChange={e => aggiorna(a.id, { ivaAgevolata: e.target.checked })}
                />
                <span>IVA 4%</span>
              </label>
              {/* @api GET /api/acquirenti/:acquirenteId/allegati/:allegatoId/download -> file stream (attachment) */}
              <button
                type="button"
                className="bop-btn bop-btn-secondary"
                onClick={() => apiDownload(`/api/acquirenti/${acquirenteId}/allegati/${a.id}/download`, a.nomeFile || a.nome || 'allegato')}
              >
                Scarica
              </button>
              <button
                className="bop-btn bop-btn-danger"
                onClick={() => elimina(a.id, a.nome)}
              >
                Elimina
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="bop-allegati-acq__upload-card">
        <div className="bop-allegati-acq__upload-title">Carica nuovo allegato</div>
        <div className="bop-allegati-acq__upload-row">
          <div className="bop-form-group">
            <label className="bop-label">File</label>
            <input
              ref={fileInputRef}
              type="file"
              className="bop-input"
              accept="application/pdf,image/jpeg,image/png,image/gif,image/webp,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={e => setFileNuovo(e.target.files?.[0] || null)}
            />
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Nome allegato</label>
            <input
              className="bop-input"
              value={nomeNuovo}
              onChange={e => setNomeNuovo(e.target.value)}
              placeholder="es. Dichiarazione IVA 4%"
            />
          </div>
          <label className="bop-allegati-acq__iva-label">
            <input
              type="checkbox"
              checked={ivaAgevolataNuovo}
              onChange={e => setIvaAgevolataNuovo(e.target.checked)}
            />
            <span>IVA 4%</span>
          </label>
          <button
            className="bop-btn bop-btn-primary"
            onClick={upload}
            disabled={saving || !fileNuovo || !nomeNuovo.trim()}
          >
            {saving ? 'Carico…' : 'Carica'}
          </button>
        </div>
        <div className="bop-text-muted bop-allegati-acq__upload-help">
          Formati accettati: PDF, JPG, PNG, GIF, WebP, DOC/DOCX, XLS/XLSX. Max 20 MB.
        </div>
      </div>
    </div>
  );
}
